export interface AppointmentWorkflowLike {
  workflow: string;
  is_after: boolean;
}

export interface PhotoWorkflowLike {
  id: string;
}

export function canUploadBeforePhotos(status: string, workStarted: boolean): boolean {
  return status === "inprogress" && !workStarted;
}

function isAwaitingClockIn(status: string): boolean {
  return status === "accepted" || status === "goback";
}

export function getWorkflowPhoto<T extends AppointmentWorkflowLike>(
  appointmentWorkflows: T[],
  workflowId: string,
  isAfter: boolean
): T | undefined {
  return appointmentWorkflows.find(
    (workflow) => workflow.workflow === workflowId && workflow.is_after === isAfter
  );
}

export function getMissingWorkflows<T extends PhotoWorkflowLike>(
  serviceWorkflows: T[],
  appointmentWorkflows: AppointmentWorkflowLike[],
  isAfter: boolean
): T[] {
  return serviceWorkflows.filter(
    (workflow) => !getWorkflowPhoto(appointmentWorkflows, workflow.id, isAfter)
  );
}

export function allBeforePhotosUploaded(
  serviceWorkflows: PhotoWorkflowLike[],
  appointmentWorkflows: AppointmentWorkflowLike[]
): boolean {
  return getMissingWorkflows(serviceWorkflows, appointmentWorkflows, false).length === 0;
}

export function getChecklistLockMessage(
  status: string,
  beforePhotosComplete: boolean,
  workStarted: boolean
): string | null {
  if (status !== "inprogress") return "Start work to begin checking tasks";
  if (!beforePhotosComplete) return "Upload all before photos to unlock the task checklist";
  if (!workStarted) return "Tap Begin Tasks to lock before photos and unlock the checklist";
  return null;
}

export function getBeforePhotoLockMessage(status: string, workStarted: boolean): string | null {
  if (canUploadBeforePhotos(status, workStarted)) return null;
  if (status === "inprogress") return "Before photos are locked once tasks begin";
  if (isAwaitingClockIn(status)) return "Start work to upload before photos";
  return "Accept the appointment to upload before photos";
}

export interface AfterPhotoGateInput {
  status: string;
  workStarted: boolean;
  hasBeforePhoto: boolean;
  checklistLoaded: boolean;
  checklistComplete: boolean;
}

export function getAfterPhotoLockMessage(input: AfterPhotoGateInput): string | null {
  if (input.status !== "inprogress") {
    return isAwaitingClockIn(input.status)
      ? "Start work to unlock after photos"
      : "Accept the appointment to upload photos";
  }
  if (!input.hasBeforePhoto) return "Upload this area's before photo first";
  if (!input.workStarted) return "Begin tasks to unlock after photos";
  if (!input.checklistLoaded) return "After photos unlock once the task checklist loads";
  if (!input.checklistComplete) return "Complete all service tasks to unlock after photos";
  return null;
}

export interface MissingPhotoSummary<T extends PhotoWorkflowLike = PhotoWorkflowLike> {
  missingBefore: T[];
  missingAfter: T[];
  message: string | null;
}

export function getMissingPhotoSummary<T extends PhotoWorkflowLike>(
  serviceWorkflows: T[],
  appointmentWorkflows: AppointmentWorkflowLike[]
): MissingPhotoSummary<T> {
  const missingBefore = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, false);
  const missingAfter = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, true);

  const missingTypes = [
    missingBefore.length > 0 ? "before" : null,
    missingAfter.length > 0 ? "after" : null,
  ]
    .filter(Boolean)
    .join(" and ");

  return {
    missingBefore,
    missingAfter,
    message: missingTypes ? `Upload all ${missingTypes} photos before completing work.` : null,
  };
}
